<template>
  <div class="stats_cards">
    <!-- 时间筛选区：快捷标签与时间选择器联动 -->
    <div class="stats_header">
      <div class="date_tags">
        <span
          v-for="tag in dateTags"
          :key="tag.value"
          :class="['date_tag', { active: activeDateTag === tag.value }]"
          @click="handleTagClick(tag.value)"
        >
          {{ tag.label }}
        </span>
      </div>
      <a-range-picker
        v-model:value="dateRange"
        class="date_picker"
        :placeholder="['开始日期', '结束日期']"
        format="YYYY-MM-DD"
        :ranges="rangePresets"
        :disabled-date="disabledDate"
        :allow-clear="false"
        @change="handleRangeChange"
        @calendar-change="handleCalendarChange"
        @open-change="handleOpenChange"
      />
      <a-button @click="handleReset">重置</a-button>
    </div>

    <!-- 统计卡片 -->
    <div class="cards_row">
      <div class="stat_card" v-for="card in cardList" :key="card.key">
        <div class="stat_label">
          {{ card.label }}
          <el-tooltip :content="card.tips" placement="bottom">
            <QuestionCircleFilled class="stat_label_icon" />
          </el-tooltip>
        </div>
        <div class="stat_value">{{ stats[card.key] }}{{ card.suffix }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onActivated, onDeactivated, onBeforeUnmount } from 'vue'
import moment from 'moment'
import { getVisitsStatistics } from '@/api/workPlace/visits'
import { QuestionCircleFilled } from '@ant-design/icons-vue'

const stats = ref({})

// 轮询刷新定时器：每 5 秒刷新一次
const REFRESH_INTERVAL = 5000
let refreshTimer = null

// 停止轮询
function stopTimer() {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
}

// 开始（或重启）轮询
function startTimer() {
  stopTimer()
  refreshTimer = setInterval(() => {
    emitChange()
  }, REFRESH_INTERVAL)
}

// 统计卡片配置（数据驱动）
const cardList = [
  { label: '待回访', key: 'pendingCount', tips: '未进行实际主动回访行为产生：给患者拨打电话' },
  { label: '进行中', key: 'processingCount', tips: '在回访池已拨打患者电话' },
  {
    label: '已回访',
    key: 'completedCount',
    tips: '已拨打患者电话且接通或拨打患者电话且发起私聊患者已读'
  },
  { label: '接通率', key: 'connectRate', suffix: '%', tips: '回访期间内，拨打患者电话且接通' },
  {
    label: '转化率',
    key: 'conversionRate',
    suffix: '%',
    tips: '回访期间内，完成拨打电话且购方或生成新的预约单'
  },
  {
    label: '效益金额',
    key: 'totalAmount',
    tips: '达成回访任务（完成回访时间 + 7 天）的购方合计总金额，单位：元'
  }
]

// 快捷标签：今日 / 昨日 / 本周 / 上周
const dateTags = [
  { label: '今日', value: 'today' },
  { label: '昨日', value: 'yesterday' },
  { label: '本周', value: 'week' },
  { label: '上周', value: 'lastWeek' }
]

// 当前选中的快捷标签
const activeDateTag = ref('today')

// 日期范围 [start, end]（moment 对象）
const dateRange = ref(getPresetRange('today'))

// 时间选择器快捷筛选：今天 / 昨天 / 本周 / 上周 / 本月 / 上月
// 使用函数形式，保证点击时取到最新时间
const rangePresets = {
  今天: () => getPresetRange('today'),
  昨天: () => getPresetRange('yesterday'),
  本周: () => getPresetRange('week'),
  上周: () => getPresetRange('lastWeek'),
  本月: () => getPresetRange('month'),
  上月: () => getPresetRange('lastMonth')
}

/**
 * 计算快捷区间（以周一为一周起始）
 * @param {string} key - today | yesterday | week | lastWeek | month | lastMonth
 * @returns {[moment, moment]}
 */
function getPresetRange(key) {
  const today = moment().startOf('day')
  switch (key) {
    case 'today':
      return [today, moment().endOf('day')]
    case 'yesterday':
      return [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')]
    case 'week': {
      // 本周：周一 ~ 周日
      const monday = today.clone().subtract((today.day() + 6) % 7, 'days')
      return [monday, monday.clone().add(6, 'days').endOf('day')]
    }
    case 'lastWeek': {
      // 上周：周一 ~ 周日
      const monday = today
        .clone()
        .subtract((today.day() + 6) % 7, 'days')
        .subtract(7, 'days')
      return [monday, monday.clone().add(6, 'days').endOf('day')]
    }
    case 'month':
      return [moment().startOf('month'), moment().endOf('month')]
    case 'lastMonth':
      return [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month')
      ]
    default:
      return [today, moment().endOf('day')]
  }
}

/**
 * 判断给定日期范围命中的快捷标签
 * @param {[moment, moment]} range
 * @returns {string} 命中的标签 value，未命中返回空串
 */
function matchTag(range) {
  if (!range || !range[0] || !range[1]) return ''
  const tagKeys = ['today', 'yesterday', 'week', 'lastWeek']
  for (const key of tagKeys) {
    const preset = getPresetRange(key)
    if (range[0].isSame(preset[0], 'day') && range[1].isSame(preset[1], 'day')) {
      return key
    }
  }
  return ''
}

// 点击快捷标签：同步更新时间选择器
function handleTagClick(value) {
  if (activeDateTag.value === value) return
  activeDateTag.value = value
  dateRange.value = getPresetRange(value)
  emitChange(value)
  startTimer()
}

// 时间选择器变化：同步更新快捷标签高亮
function handleRangeChange(dates) {
  activeDateTag.value = matchTag(dates)
  emitChange(activeDateTag.value, dates)
  startTimer()
}

async function emitChange(_, dates) {
  const range = dates || dateRange.value
  const strings =
    range && range[0] && range[1]
      ? [range[0].format('YYYY-MM-DD HH:mm:ss'), range[1].format('YYYY-MM-DD HH:mm:ss')]
      : ['', '']

  const [startTime, endTime] = strings

  const res = await getVisitsStatistics({
    startTime,
    endTime
  })

  stats.value = res
}

// 记录用户在面板中选中的第一个日期（用于限制跨度一年）
const selectedStartDate = ref(null)

const disabledDate = current => {
  if (!current) return false

  // 不能选择今天以后的日期
  if (current > moment().endOf('day')) return true

  // 已选中起始日期时，限制可选范围不超过一年
  if (selectedStartDate.value) {
    const start = moment(selectedStartDate.value)
    if (current < start.clone().subtract(1, 'year') || current > start.clone().add(1, 'year')) {
      return true
    }
  }

  return false
}

// 用户在日历面板选中第一个日期时触发
function handleCalendarChange(dates) {
  if (dates && dates[0]) {
    selectedStartDate.value = dates[0]
  }
}

// 面板打开/关闭时重置起始日期
function handleOpenChange(open) {
  if (open) {
    selectedStartDate.value = null
  }
}

// 重置快捷标签和时间选择器
function handleReset() {
  activeDateTag.value = 'today'
  dateRange.value = getPresetRange('today')
  emitChange(activeDateTag.value)
  startTimer()
}

onMounted(() => {
  // 初始化触发一次，便于父组件获取默认区间
  emitChange(activeDateTag.value)
})

// keep-alive 缓存：页面重新展示时恢复轮询
onActivated(() => {
  startTimer()
})

// keep-alive 缓存：页面被隐藏时停止轮询
onDeactivated(() => {
  stopTimer()
})

// 页面销毁时清除定时器，防止内存泄漏
onBeforeUnmount(() => {
  stopTimer()
})
</script>

<style scoped lang="scss">
.stats_cards {
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: #fff;
  padding: 16px 24px;

  .stats_header {
    display: flex;
    align-items: center;
    gap: 16px;

    .date_tags {
      display: flex;
      gap: 60px;
      border-bottom: 1px solid #f4f4f4;
      padding-right: 8px;
      .date_tag {
        position: relative;
        padding-bottom: 9px;
        font-size: 16px;
        font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif;
        font-weight: 400;
        line-height: 22px;
        color: #333;
        cursor: pointer;
        &:hover {
          color: #1890ff;
        }
        &.active {
          color: #1890ff;
          font-weight: 500;
          // 激活态下划线指示器：40px × 3px
          &::after {
            content: '';
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            bottom: -1px;
            width: 40px;
            height: 3px;
            background: #1890ff;
          }
        }
      }
    }
    .date_picker {
      width: 280px;
    }
  }

  .cards_row {
    display: flex;
    gap: 12px;

    .stat_card {
      flex: 1;
      border-radius: 4px;
      border: 1px solid rgba(24, 144, 255, 0.15);
      background: #f2f9ff;
      padding: 12px 16px;
      .stat_label {
        font-size: 14px;
        color: #666;
        margin-bottom: 6px;
        display: flex;
        align-items: center;
        gap: 4px;

        .stat_label_icon {
          color: #ccc;
          font-size: 14px;
        }
      }
      .stat_value {
        font-size: 22px;
        font-weight: 500;
        color: #1890ff;
      }
    }
  }
}
</style>
